import type { ViewConfigurationStoreState } from "./viewConfigurationStore.ts";
import { difference, identity, isNil, omit } from "lodash-es";
import { hasData } from "../fields/fields.ts";
import type { FormVersion } from "../state/formVersion.tsx";
import type { ColumnSort } from "@tanstack/solid-table";
import type { Filter } from "../pages/customer/form/filter.ts";
import logger from "./logger.ts";
import { Scope } from "../types.ts";

type Type = "field" | "variable";

export type SavedViewConfigurationStoreState = {
  sorting: (ColumnSort & { type: Type })[];
  columnOrder: { name: string; type: Type }[];
  columnVisibility: Record<string, { visible: boolean; type: Type }>;
  filters: (Omit<Filter, "path"> & { path: { path: string; type: Type } })[];
};

const serializeEntry = <T, Result>(
  entry: T,
  formVersion: FormVersion,
  getPath: (entry: T) => string,
  serializeEntry: (entry: T, type: Type, newPath: string) => Result,
) => {
  if (getPath(entry).startsWith("data.")) {
    const formField = formVersion.fields
      .filter((field) => field.scope === Scope.ROOT)
      .filter(hasData)
      .find((field) => `data.${field.properties.data_name}` === getPath(entry));
    if (isNil(formField)) {
      throw new Error("Field can't be found in this formVersion");
    }
    return serializeEntry(entry, "field", formField.uid.toString());
  }
  return serializeEntry(entry, "variable", getPath(entry));
};

const deserializeEntry = <T, Result>(
  entry: T,
  formVersion: FormVersion,
  getType: (entry: T) => Type,
  getPathOrUid: (entry: T) => string,
  deserializeEntry: (entry: T, path: string) => Result,
) => {
  try {
    if (getType(entry) == "field") {
      const formField = formVersion.fields.filter(hasData).find((field) => field.uid.equals(getPathOrUid(entry)));
      if (isNil(formField)) {
        // The uid saved in localStorage is not in this formVersion (anymore)
        return;
      }
      return deserializeEntry(entry, `data.${formField.properties.data_name}`);
    }
    return deserializeEntry(entry, getPathOrUid(entry));
  } catch (e) {
    // For some reason we could not deserialize this entry. This could be because it uses an old format.
    // To make sure the submission table does not crash based on data in the localStorage, we will discard this entry.
    logger.warn("Unable to deserialize entry", e);
    return;
  }
};

export const serializeConfigurationStore = (formVersion: FormVersion) => (value: ViewConfigurationStoreState) => {
  const serializedConfigurationStore = {
    ...value,
    columnOrder: value.columnOrder.map((column) =>
      serializeEntry(column, formVersion, identity, (_, type, path) => ({ name: path, type })),
    ),
    columnVisibility: Object.fromEntries(
      Object.entries(value.columnVisibility).map(([key, value]) =>
        serializeEntry(
          { key, value },
          formVersion,
          (entry) => entry.key,
          (entry, type, path) => [path, { visible: entry.value, type }],
        ),
      ),
    ),
    filters: value.filters.map((filter) =>
      serializeEntry(
        filter,
        formVersion,
        (entry) => entry.path,
        (entry, type, path) => ({
          ...entry,
          path: {
            path,
            type,
          },
        }),
      ),
    ),
    sorting: value.sorting.map((sort) =>
      serializeEntry(
        sort,
        formVersion,
        (entry) => entry.id,
        (entry, type, path) => ({ ...entry, id: path, type }),
      ),
    ),
  };

  return JSON.stringify(serializedConfigurationStore);
};

export const deserializeConfigurationStore =
  (formVersion: FormVersion) =>
  (value: string): ViewConfigurationStoreState => {
    const parsedConfigurationStore = JSON.parse(value) as SavedViewConfigurationStoreState;

    const formFieldsWithData = formVersion.fields
      .filter((formField) => formField.scope === Scope.ROOT)
      .filter(hasData)
      .map((formField) => `data.${formField.properties.data_name}`);

    const columnOrder = parsedConfigurationStore.columnOrder
      .map((column) =>
        deserializeEntry(
          column,
          formVersion,
          (entry) => entry.type,
          (entry) => entry.name,
          (_, path) => path,
        ),
      )
      .filter((column) => !isNil(column));

    return {
      ...parsedConfigurationStore,
      columnOrder: [...columnOrder, ...difference(formFieldsWithData, columnOrder)],
      columnVisibility: Object.fromEntries(
        Object.entries(parsedConfigurationStore.columnVisibility)
          .map(([key, value]) =>
            deserializeEntry(
              { key, value },
              formVersion,
              (entry) => entry.value.type,
              (entry) => entry.key,
              (entry, path) => [path, entry.value.visible],
            ),
          )
          .filter((column) => !isNil(column)),
      ),
      filters: parsedConfigurationStore.filters
        .map((filter) =>
          deserializeEntry(
            filter,
            formVersion,
            (entry) => entry.path.type,
            (entry) => entry.path.path,
            (entry, path) => ({
              ...entry,
              path,
            }),
          ),
        )
        .filter((filter) => !isNil(filter)),
      sorting: parsedConfigurationStore.sorting
        .map((sort) =>
          deserializeEntry(
            sort,
            formVersion,
            (entry) => entry.type,
            (entry) => entry.id,
            (entry, id) => ({ ...(omit(entry, ["type"]) as ColumnSort), id }),
          ),
        )
        .filter((sort) => !isNil(sort)),
    };
  };
